import { Controller } from "@hotwired/stimulus"

// Shared copy-to-clipboard controller, used across 10 views (cards, projects,
// objectives, planning, api tokens, response previews, tracking settings,
// mission task preview, shared links).
//
// navigator.clipboard only exists in a secure context (HTTPS, or localhost).
// On an insecure origin -- e.g. the review server, http://<lan-ip>:port --
// window.isSecureContext is false and navigator.clipboard is undefined, so
// calling it used to throw synchronously and the click produced zero visible
// feedback (card #3613 round 17). copy() now falls through three paths so a
// click always does *something* visible:
//   1. navigator.clipboard.writeText -- the normal modern-browser path.
//   2. a hidden textarea + document.execCommand("copy") -- works in an
//      insecure context in most browsers.
//   3. window.prompt with the value pre-filled -- last resort when neither
//      API is available/succeeds, same pattern as
//      pwa/diagnostic_log_controller.js's copyLog(). A prompt isn't a
//      confirmed copy, so it skips the success-confirmation animation.
export default class extends Controller {
  static targets = ["source","action"];

  async copy() {
    const text = this.sourceTarget.innerText

    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(text)
        this.showCopiedFeedback()
        return
      } catch {
        // Fall through to the textarea/execCommand fallback below.
      }
    }

    if (this.copyWithExecCommand(text)) {
      this.showCopiedFeedback()
      return
    }

    // Last resort -- surface the value so the user can select and copy it by
    // hand. Not a confirmed copy, so no success animation here.
    window.prompt("Copy this link:", text)
  }

  // Hidden off-screen textarea + the legacy execCommand API -- the standard
  // fallback for insecure contexts where navigator.clipboard doesn't exist.
  // Returns whether the copy actually succeeded.
  copyWithExecCommand(text) {
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.position = "fixed"
    textarea.style.left = "-9999px"
    // A native <dialog> opened with showModal() (e.g. the Configure panel)
    // makes everything outside it inert, so an element appended to
    // document.body while a dialog is open can never receive focus.
    // Append inside the open dialog when there is one -- same call pattern
    // gallery_controller.js already uses for its own modal portalling.
    const container = this.element.closest("dialog[open]") || document.body
    container.appendChild(textarea)
    // focus() before select() -- select() alone doesn't reliably move focus
    // to the textarea in every browser, and execCommand("copy") copies
    // whatever's selected in the FOCUSED element, not just the target of the
    // most recent .select() call.
    textarea.focus()
    textarea.select()

    let succeeded = false
    try {
      succeeded = document.execCommand("copy")
    } catch {
      succeeded = false
    }

    textarea.remove()
    return succeeded
  }

  // The app's existing confirmation pattern -- icon swap for small icon
  // buttons, text swap for regular buttons. Unchanged from before this fix,
  // just extracted so both the primary and execCommand fallback paths can
  // trigger it.
  showCopiedFeedback() {
    // Check if the button is small (has specific width/height classes)
    const isSmallButton = (this.actionTarget.classList.contains('w-5') && this.actionTarget.classList.contains('h-5')) ||
                         this.actionTarget.classList.contains('size-5') ||
                         this.actionTarget.classList.contains('size-6');

    if (isSmallButton) {
      // For small icon buttons, change icon to green checkmark
      const originalIcon = this.actionTarget.innerHTML;
      this.actionTarget.innerHTML = `<svg class="shrink-0 mr-1.5 h-4 w-4 text-green-500 stroke-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>`;
      this.actionTarget.classList.add("text-green-500")
      setTimeout(() => {
        this.actionTarget.innerHTML = originalIcon;
        this.actionTarget.classList.remove("text-green-500")
      }, 1500)
    } else {
      // For regular buttons, change text to "Copied!", then restore whatever
      // was there before -- captured fresh on every call rather than
      // hardcoded back to "Copy" (card #4707: the Sprint Board's "Copy for
      // AI" button has its own label + icon, not the plain "Copy" text every
      // other caller of this branch happened to use so far; restoring the
      // original innerHTML keeps that intact instead of silently renaming
      // the button after its first click).
      const originalContent = this.actionTarget.innerHTML
      this.actionTarget.innerText = "Copied!"
      this.actionTarget.classList.add("animate-pulse")
      setTimeout(() => {
        this.actionTarget.innerHTML = originalContent
        this.actionTarget.classList.remove("animate-pulse")
      }, 2000)
    }
  }
}